/**
 * @author Harshitha.k
 * @description 
 */

import './reportConfig.css';
import React, { useState, useEffect } from 'react';
import { Input, Form, Select, Collapse, Button, message } from 'antd';
import ChildrenColumnCustomization from './ChildrenColumnCustomization';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { v4 as uuid } from 'uuid'; // Using uuid v4 for generating unique IDs

const { Option } = Select;
const { Panel } = Collapse;

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

const grid = 8;

const getListStyle = (isDraggingOver) => ({
  background: isDraggingOver ? 'lightblue' : 'lightgrey',
  padding: grid,
  marginTop: 14,
  marginBottom: 14
});

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: 'none',
  background: isDragging ? 'lightgrey' : 'grey',
  ...draggableStyle
});

const ColumnCustomization = ({ columnsProperties: initialColumnsProperties, jsonSaveObject }) => {
  const [columnsProperties, setColumnsProperties] = useState(initialColumnsProperties || []);
  const [activeKey, setActiveKey] = useState([]);
  const dragDisabled = useState(false);

  useEffect(() => {
    setColumnsProperties(initialColumnsProperties);
  }, [initialColumnsProperties]);

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const reorderedColumns = reorder(
      columnsProperties,
      result.source.index,
      result.destination.index
    );
    setColumnsProperties(reorderedColumns);
    jsonSaveObject(reorderedColumns, 'columns');
  };

  const handleFieldChange = (value, id, colName, index) => {
    const updatedColumns = columnsProperties.map((column, colIndex) => {
      if (colIndex === index && column.id === id) {
        return { ...column, [colName]: value };
      }
      return column;
    });
    setColumnsProperties(updatedColumns);
  };

  const submitChildren = (children, index) => {
    const updatedColumns = [...columnsProperties];
    updatedColumns[index]['children'] = children;
    setColumnsProperties(updatedColumns);
  };

  const columnValidation = (columnsProps, id, index) => {
    const column = columnsProps[index];
    if (column) {
      if (column.children) {
        if (column.dataIndex === '' || column.key === '') {
          message.error('Please Enter dataIndex & key');
        } else {
          jsonSaveObject(columnsProperties, 'columns');
          message.success('Column Validated Successfully');
          setActiveKey([]);
        }
      } else {
        if (column.title === '' || column.dataIndex === '' || column.key === '') {
          message.error('Please Enter All These Fields');
        } else {
          jsonSaveObject(columnsProperties, 'columns');
          message.success('Columns Validated Successfully');
          setActiveKey([]);
        }
      }
    }
  };

  const deleteColumn = (index) => {
    const updatedColumns = columnsProperties.filter((_, i) => i !== index);
    jsonSaveObject(updatedColumns, 'columns');
    message.success('Column Deleted');
    setActiveKey([]);
    setColumnsProperties(updatedColumns);
  };

  const callback = (key) => {
    if (key && key.length <= 1) {
      setActiveKey(key);
    } else if (key.length > 1) {
      setActiveKey([key[key.length - 1]]);
    }
  };

  const addChildren = (id, index) => {
    const updatedColumns = [...columnsProperties];
    const item = updatedColumns[index];

    if (item.title === '' || item.dataIndex === '' || item.key === '') {
      message.error('Please Enter All These Fields');
      return;
    }

    const newChild = {
      id: uuid(),
      title: 'Children',
      dataIndex: '',
      key: '',
      width: '100',
      className: '',
      background: ''
    };

    if (item.children) {
      item.children.push(newChild);
    } else {
      item.children = [newChild];
    }

    setColumnsProperties(updatedColumns);
  };

  const formItemLayout = {
    labelCol: {
      xs: { span: 24 },
      sm: { span: 3 }
    },
    wrapperCol: {
      xs: { span: 24 },
      sm: { span: 8 }
    }
  };

  return (
    <div className="column-div" style={{ margin: '44px 0px' }}>
      {columnsProperties.length > 0 ? (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver)}
                {...provided.droppableProps}
              >
                {columnsProperties.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.id}
                    index={index}
                    isDragDisabled={dragDisabled}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                      >
                        <Collapse bordered activeKey={activeKey} onChange={callback}>
                          <Panel header={item.title} key={index}>
                            <Form {...formItemLayout}>
                              <Form.Item label="Title :">
                                <Input
                                  value={item.title}
                                  onChange={(e) =>
                                    handleFieldChange(e.target.value, item.id, 'title', index)
                                  }
                                />
                              </Form.Item>
                              <Form.Item label="DataIndex :">
                                <Input
                                  value={item.dataIndex}
                                  onChange={(e) =>
                                    handleFieldChange(e.target.value, item.id, 'dataIndex', index)
                                  }
                                />
                              </Form.Item>
                              <Form.Item label="Key :">
                                <Input
                                  value={item.key}
                                  onChange={(e) =>
                                    handleFieldChange(e.target.value, item.id, 'key', index)
                                  }
                                />
                              </Form.Item>
                              <Form.Item label="Width :">
                                <Input
                                  style={{ width: '100%' }}
                                  value={item.width}
                                  type="number"
                                  onChange={(e) =>
                                    handleFieldChange(e.target.value, item.id, 'width', index)
                                  }
                                />
                              </Form.Item>
                              <Form.Item label="ClassName :">
                                <Select
                                  style={{ width: '100%' }}
                                  value={item.className}
                                  onSelect={(value) =>
                                    handleFieldChange(value, item.id, 'className', index)
                                  }
                                >
                                  <Option value="table-column">TABLE-COLUMN</Option>
                                </Select>
                              </Form.Item>

                              <div
                                style={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  marginLeft: '18px'
                                }}
                              >
                                <Button
                                  type="primary"
                                  style={{ margin: '2px' }}
                                  onClick={() =>
                                    columnValidation(columnsProperties, item.id, index)
                                  }
                                >
                                  Save
                                </Button>
                                <Button
                                  type="danger"
                                  style={{ margin: '2px' }}
                                  onClick={() => deleteColumn(index)}
                                >
                                  Delete
                                </Button>
                                <Button
                                  type="primary"
                                  style={{ margin: '2px' }}
                                  onClick={() => addChildren(item.id, index)}
                                >
                                  Add Children
                                </Button>
                              </div>
                              {item.children ? (
                                <div>
                                  <ChildrenColumnCustomization
                                    childProperties={item.children}
                                    submitChildren={submitChildren}
                                    index={index}
                                  />
                                </div>
                              ) : null}
                            </Form>
                          </Panel>
                        </Collapse>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      ) : null}
    </div>
  );
};

export default ColumnCustomization;